import * as R from 'ramda';
import moment from 'moment';
import i18n from 'helpers/i18n';
import messages from './messages';
import useAsync from 'hooks/useAsync';
import RouteHelpers from 'helpers/routes';
import { useState, useEffect } from 'react';
import { toastError } from 'components/Toast';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { Assessment } from 'types/api/envScales/Assessment';
import { useDCLanguages } from 'components/dropdowns/Languages';
import ConfirmationModal from 'components/Modals/ConfirmationModal';
import { ObservationCycle } from 'types/api/envScales/ObservationCycle';
import CycleControls from 'components/Measure/View/Cycles/CycleControls';

import {
  returnAssessmentToAwaiting,
  returnAssessmentToInProgress
} from 'actions/envScales/assessments';

import {
  Container,
  Wrapper,
  ButtonWrapper,
  Col,
  ColLabel,
  ColValue,
  InfoTitle,
  InfoData
} from './Styled';

import {
  IconButton,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@teachstonellc/component-library';

interface InfoProps {
  /** Assessment data. */
  data: Assessment;

  /** If `true`, a button for editing the assessment will be displayed. */
  showButton?: boolean;

  /** If `true`, the box will not have rounded corners at the bottom. */
  attachToBottom?: boolean;

  /** Observation Cycle. */
  cycle?: ObservationCycle;

  extended?: boolean;

  /**If `true` the user is admin */
  isAdmin?: boolean;

  /**If `true` the user is supervisor */
  isSupervisor?: boolean;

  /**If `true` is video observation */
  isVideoObservation?: boolean;

  /**If `true` the observation requires approval */
  requiresApproval?: boolean;
}

function InfoBox({
  data,
  showButton,
  attachToBottom,
  cycle,
  extended,
  isAdmin = false,
  isSupervisor = false,
  isVideoObservation,
  requiresApproval
}: InfoProps) {
  const languages = useDCLanguages();
  const isLessThan640px = useMediaQuery({ maxWidth: 640 });
  const [scrolled, setScrolled] = useState<boolean>(false);
  const navigate = useNavigate();
  const { run } = useAsync();

  const teachers = [...data.teachers, ...data.educators.map(e => e.name)];
  const showTeachers = extended && !R.isEmpty(teachers);

  const obsLanguages = languages
    .filter(lang => data.languages.includes(lang.key))
    .map(l => l.text)
    .join(', ');

  useEffect(() => {
    const listenScrollEvent = () => {
      const minPosition = isLessThan640px ? 180 : 305;
      const maxPosition = isLessThan640px ? 225 : 355;
      if (window.scrollY < minPosition) {
        setScrolled(false);
      } else if (window.scrollY >= maxPosition) {
        // add diff to avoid flickering
        setScrolled(true);
      }
    };

    window.addEventListener('scroll', listenScrollEvent);
    return () => window.removeEventListener('scroll', listenScrollEvent);
  }, [isLessThan640px]);

  async function onConfirmChange() {
    await run(returnAssessmentToAwaiting(data.id));
    setTimeout(() => {
      navigate(RouteHelpers.getPath('measure'));
    }, 300);
  }

  function onConfirmToInProgress() {
    run(returnAssessmentToInProgress(data.id), {
      onSuccess: () => {
        setTimeout(() => {
          navigate(RouteHelpers.getPath('measure'));
        }, 300);
      },
      onError: (error: any) => {
        toastError({ description: error.errors.status });
      }
    });
  }

  function getAgeLevel() {
    const ageLevelMap: Record<string, string> = {
      'PK-3rd': i18n.ft(messages.prek3rd),
      IT: i18n.ft(messages.it)
    };
    return ageLevelMap[data.age_level] || data.age_level;
  }

  if (R.isNil(data)) {
    return null;
  }

  return (
    <Wrapper scrolled={scrolled} extended={extended}>
      <Container
        attachToBottom={attachToBottom}
        scrolled={scrolled && !extended}
      >
        <Col>
          <ColLabel>
            <InfoTitle>{i18n.ft(messages.observationDate)}</InfoTitle>
            <InfoTitle>{i18n.ft(messages.center_name)}</InfoTitle>
            {extended && <InfoTitle>{i18n.ft(messages.observer)}</InfoTitle>}
            {showTeachers && (
              <InfoTitle>{i18n.ft(messages.educators)}</InfoTitle>
            )}
          </ColLabel>
          <ColValue>
            <InfoData>
              {data && moment(data.taken_at).format('MM/DD/YYYY')}
            </InfoData>
            <InfoData>{data.center_name}</InfoData>
            {extended && <InfoData>{data.observer_name}</InfoData>}
            {showTeachers && <InfoData>{teachers.join(', ')}</InfoData>}
          </ColValue>
        </Col>

        {!isLessThan640px && (
          <Col>
            <ColLabel>
              <InfoTitle>{i18n.ft(messages.observationTool)}</InfoTitle>
              <InfoTitle>{i18n.ft(messages.classroom)}</InfoTitle>
              <InfoTitle>{i18n.ft(messages.language)}</InfoTitle>
              {data.job_id && <InfoTitle>{i18n.ft(messages.jobId)}</InfoTitle>}
            </ColLabel>
            <ColValue>
              <InfoData>{getAgeLevel()}</InfoData>
              <InfoData>{data.classroom_name}</InfoData>
              <InfoData>{obsLanguages}</InfoData>
              {data.job_id && <InfoData>{data.job_id}</InfoData>}
            </ColValue>
          </Col>
        )}

        <Col>
          <ButtonWrapper>
            {(showButton ||
              (isAdmin && data.status === 'awaiting_approval')) && (
              <IconButton
                className="hover:text-ds-icon-hover"
                onClick={() =>
                  navigate(
                    RouteHelpers.getPath('measure-view-edit', { id: data.id })
                  )
                }
                aria-label={i18n.ft(messages.editObservationAria)}
                icon="fa-solid fa-pencil"
              />
            )}
            {(isAdmin || isSupervisor) && data.status === 'completed' && (
              <DropdownMenu>
                <DropdownMenuTrigger>
                  <IconButton
                    className="hover:text-ds-icon-hover"
                    aria-label="expose option to return to approval"
                    icon="fa-solid fa-ellipsis"
                  />
                </DropdownMenuTrigger>
                {!isVideoObservation && !requiresApproval && (
                  <ConfirmationModal
                    title={i18n.ft(messages.modal.titleInProgress)}
                    message={i18n.ft(messages.modal.warningInProgress)}
                    confirmButton={i18n.ft(messages.modal.confirm)}
                    icon="tasks"
                    triggerNode={
                      <DropdownMenuContent>
                        <DropdownMenuItem>
                          {i18n.ft(messages.modal.titleInProgress)}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    }
                    onConfirm={({ closeModal }) => {
                      onConfirmToInProgress();
                      closeModal();
                    }}
                  />
                )}
                {requiresApproval && (
                  <ConfirmationModal
                    title={i18n.ft(messages.modal.title)}
                    message={i18n.ft(messages.modal.warning)}
                    confirmButton={i18n.ft(messages.modal.confirm)}
                    icon="tasks"
                    triggerNode={
                      <DropdownMenuContent>
                        <DropdownMenuItem>
                          {i18n.ft(messages.return)}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    }
                    onConfirm={onConfirmChange}
                  />
                )}
              </DropdownMenu>
            )}
          </ButtonWrapper>
        </Col>
      </Container>

      {cycle && <CycleControls assessment={data} scrolled={scrolled} />}
    </Wrapper>
  );
}

export default InfoBox;
